import type { Locale } from './types'

/**
 * Every public URL in one place.
 *
 * The language lives in the path (/uk/..., /en/...) rather than in a cookie or
 * a query string. That gives each language its own indexable address, which is
 * what hreflang needs to work, and means a shared link opens in the language
 * it was shared in.
 */
export const paths = {
  home: (locale: Locale) => `/${locale}`,
  gallery: (locale: Locale) => `/${locale}/gallery`,
  category: (locale: Locale, slug: string) => `/${locale}/gallery/${slug}`,
  /**
   * The address of one piece: /uk/work/42-kit-bandyt
   *
   * The number is what identifies it; the slug after it is for readers and for
   * search engines. Renaming a piece changes the slug but not the number, so
   * links shared earlier keep working and simply show the new name.
   */
  artwork: (locale: Locale, ref: { publicId: number; slug: string }) =>
    `/${locale}/work/${artworkRef(ref)}`,
  about: (locale: Locale) => `/${locale}/about`,
  contacts: (locale: Locale) => `/${locale}/contacts`,
} as const

/**
 * Builds the URL segment for one piece: "42-kit-bandyt".
 *
 * The fallback matters. If the API is an older build that does not send
 * publicId yet, the naive version produces "undefined-kit-bandyt" — a link
 * that looks plausible and is quietly broken. Falling back to the slug alone
 * keeps it working, because the API still resolves a bare slug.
 */
export function artworkRef({ publicId, slug }: { publicId?: number; slug: string }): string {
  if (typeof publicId !== 'number' || !Number.isFinite(publicId)) {
    return slug
  }
  return slug ? `${publicId}-${slug}` : String(publicId)
}

export const adminPaths = {
  root: '/admin',
  login: '/admin/login',
  categories: '/admin/categories',
  artworks: '/admin/artworks',
  newArtwork: '/admin/artworks/new',
  artwork: (id: string) => `/admin/artworks/${id}`,
  media: '/admin/media',
  pages: '/admin/pages',
  page: (slug: string) => `/admin/pages/${slug}`,
  messages: '/admin/messages',
  users: '/admin/users',
  audit: '/admin/audit',
} as const

/**
 * Rewrites the current URL into another language, so the switcher lands on the
 * same page rather than sending everyone back to the home page.
 */
export function switchLocalePath(pathname: string, next: Locale): string {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 0) return `/${next}`
  segments[0] = next
  return `/${segments.join('/')}`
}

/** The path with the language prefix removed, e.g. "/uk/about" -> "/about". */
export function stripLocale(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean)
  segments.shift()
  return segments.length ? `/${segments.join('/')}` : '/'
}
